import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { SystemDescriptions } from "src/common/descriptions"
import { CancelConfirmationModal } from "src/common/descriptions/components/cancelConfirmationModal"
import { ExpenditureRecord } from "src/common/models/expenditureRecord"
import { PageCard } from "src/common/styles/styles"
import { Urls } from "src/common/urls"
import { Loader } from "src/components/Loader"
import { ModalSuccess } from "src/components/ModalSuccess"
import { CREATE_EXPENDITURE_RECORD_API } from "src/features/AccountPayable/state/actionTypes"
import { AccountPayablesError } from "src/features/AccountPayable/state/types"
import { RootState } from "src/state/reducer"
import { ExpenditureRecordForm } from "../ExpenditureRecordForm"
import { createExpenditureRecordApi } from "src/features/AccountPayable/state/action"
import useUserPermission from "src/hooks/useUserPermissions"
import { USER_SCOPES } from "src/hooks/UserPermission"

interface ReduxProps {
    error?: AccountPayablesError
    createExpenditureRecordSuccess: boolean
    isCreatingRecord: boolean
}

export const CreateExpenditureRecord = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { validateUserScopeForPage } = useUserPermission()

    useEffect(() => {
        validateUserScopeForPage(USER_SCOPES.EXPENDITURE_RECORD_CREATE)
    }, [])

    const descriptions = SystemDescriptions.PAGES.ACCOUNT_PAYABLES.CREATE_EXPENDITURE_RECORD

    const [modalConfirmationOpen, setModalConfirmationOpen] = useState<boolean>(false)
    const [modalCancelOpen, setModalCancelOpen] = useState<boolean>(false)
    const [formValues, setFormValues] = useState<ExpenditureRecord | undefined>()
    const [actionButtonClicked, setActionButtonClicked] = useState<boolean>(false)

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        error: state.accountPayables.error,
        createExpenditureRecordSuccess: state.accountPayables.createExpenditureRecordSuccess,
        isCreatingRecord: serviceLoading(state, [CREATE_EXPENDITURE_RECORD_API]),
    }))

    const onFinish = (customer: Partial<ExpenditureRecord>) => {
        setFormValues(customer as ExpenditureRecord)
        setModalConfirmationOpen(true)
    }

    const onCancel = () => {
        setModalCancelOpen(true)
    }

    const handleConfirmationAccept = () => {
        setModalConfirmationOpen(false)
        setActionButtonClicked(true)
        dispatch(createExpenditureRecordApi(formValues as ExpenditureRecord))
    }

    const handleCancelAccept = () => {
        navigate(Urls.FRONTEND.EXPENDITURE_RECORD.RECORDS)
    }

    const renderModals = () => (
        <>
            <CancelConfirmationModal
                isVisible={modalConfirmationOpen}
                onAccept={handleConfirmationAccept}
                onCancel={() => {
                    setModalConfirmationOpen(false)
                }}
                title={descriptions.CONFIRM_MODAL.TITLE}
                okText={descriptions.CONFIRM_MODAL.ACTION_BUTTON}
                cancelText={descriptions.CONFIRM_MODAL.CANCEL_BUTTON}
                description={descriptions.CONFIRM_MODAL.DESCRIPTION}
            />
            <CancelConfirmationModal
                isVisible={modalCancelOpen}
                onAccept={handleCancelAccept}
                onCancel={() => { setModalCancelOpen(false) }}
                title={descriptions.CANCEL_MODAL.TITLE}
                okText={descriptions.CANCEL_MODAL.ACTION_BUTTON}
                cancelText={descriptions.CANCEL_MODAL.CANCEL_BUTTON}
                description={descriptions.CANCEL_MODAL.DESCRIPTION}
            />
            <ModalSuccess
                isVisible={(reduxProps.createExpenditureRecordSuccess) && actionButtonClicked}
                title={descriptions.SUCCESS_MODAL.TITLE}
                description={descriptions.SUCCESS_MODAL.DESCRIPTION}
                onPrimaryAction={handleCancelAccept}
                onSecondaryActiont={handleCancelAccept}
                primaryLabel={descriptions.SUCCESS_MODAL.PRIMARY_LABEL}
                secondaryLabel={descriptions.SUCCESS_MODAL.SECONDARY_LABEL}
                hideSecondaryButton={true}
            />
        </>
    )

    const renderLoader = () => (
        <Loader
            isVisible={reduxProps.isCreatingRecord}
            title={descriptions.MAIN_ACTION_TITLE}
            description={descriptions.MAIN_ACTION_DESCRIPTION}
        />
    )

    return (
        <>
            <PageCard
                size="small"
                title={descriptions.TITLE}
            >
                <ExpenditureRecordForm
                    onFinish={onFinish}
                    onCancel={onCancel}
                />
            </PageCard>
            {renderModals()}
            {renderLoader()}
        </>
    )
}
